// LLM generated - Model Claude 4 Sonnet
import { useRef, useMemo } from 'react';

import { useIsTablet } from '~/domains/common/hooks/mediaQueries';
import { Button } from '~/domains/design-system/Button';
import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import DialogContainer from '~/domains/design-system/components/Dialog/DialogContainer';
import { Text } from '~/domains/design-system/Text';
import { TextInput } from '~/domains/design-system/TextInput';
import { Title } from '~/domains/design-system/Title';

import { useStyles } from './TextInputModal.styles';
import type { TextInputModalProps } from './types';

function TextInputModal({
  isOpen,
  onClose,
  onSubmit,
  value = '',
  onChange,
  icon,
  title,
  titleVariant = 'headline-small',
  titleColor,
  subtitle,
  placeholder,
  error: externalError,
  inputLabel,
  autoFocus,
  buttonText,
  buttonVariant = 'primary-fill',
  buttonDisabled = false,
  buttonLoading = false,
  classes,
  modalType = {
    desktop: 'modal',
    mobile: 'drawer',
  },
  validate,
}: TextInputModalProps) {
  const { classes: baseClasses, cx } = useStyles();
  const isMouseDownInsideRef = useRef(false);
  const isMobile = useIsTablet();

  // Default autoFocus to false on mobile (to prevent keyboard auto-opening), true on desktop
  const shouldAutoFocus = autoFocus ?? !isMobile;

  // Run validation if provided
  const validationError = useMemo(() => {
    if (validate) {
      return validate(value);
    }
    return null;
  }, [validate, value]);

  // Use external error if provided, otherwise use validation error
  const error = externalError ?? validationError;

  // Disable button if there's a validation error or if explicitly disabled
  const isButtonDisabled = buttonDisabled || !!validationError;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isButtonDisabled && !buttonLoading) {
      onSubmit();
    }
  };

  // Track when mousedown happens inside the dialog
  const handleMouseDown = (e: React.MouseEvent) => {
    isMouseDownInsideRef.current = true;
    e.stopPropagation();
  };

  // Track when mouseup happens inside the dialog
  const handleMouseUp = () => {
    isMouseDownInsideRef.current = false;
  };

  // Prevent close if mousedown started inside
  const handleClose = () => {
    if (!isMouseDownInsideRef.current) {
      onClose();
    }
    isMouseDownInsideRef.current = false;
  };

  return (
    <DialogContainer
      isOpen={isOpen}
      onClose={handleClose}
      className={baseClasses.container}
      lockScroll
    >
      <Dialog type={modalType} isOpen={isOpen} onClose={handleClose}>
        <DialogBody
          className={cx(baseClasses.body, classes?.body)}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
        >
          <div className={cx(baseClasses.contentWrapper, classes?.contentWrapper)}>
            <div className={cx(baseClasses.headerSection, classes?.headerSection)}>
              {icon && <div className={cx(baseClasses.iconWrapper, classes?.icon)}>{icon}</div>}

              <Title
                variant={titleVariant}
                className={cx(baseClasses.title, classes?.title)}
                c={titleColor}
              >
                {title}
              </Title>

              {subtitle && (
                <div className={cx(baseClasses.subtitle, classes?.subtitle)}>
                  <Text variant="body-large">{subtitle}</Text>
                </div>
              )}
            </div>

            <div className={cx(baseClasses.formWrapper, classes?.formWrapper)}>
              <TextInput
                value={value}
                onChange={onChange}
                variant="filled"
                placeholder={placeholder}
                label={inputLabel}
                autoFocus={shouldAutoFocus}
                error={error}
                onKeyDown={handleKeyDown}
                className={classes?.input}
              />

              <Button
                variant={buttonVariant}
                onClick={onSubmit}
                loading={buttonLoading}
                disabled={isButtonDisabled}
                fullWidth
                className={classes?.button}
              >
                {buttonText}
              </Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>
    </DialogContainer>
  );
}

export default TextInputModal;
